import { useEffect, useState, useCallback, Fragment } from "react";
import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ZAxis } from "recharts";
import { ChevronRight, ChevronDown, Search } from "lucide-react";
import api from "@/lib/api";
import { FiltersBar, Kpi, Spark, Loading, ExpBtn, CARD, LBL, SEL, fmtAht, fmtNum, fmtDelta, fq, thrColor, downloadPost } from "./axiom";

const BUCKET = { high: "#F43F5E", above: "#F59E0B", near: "#7a8fb5", below: "#10B981" };

const DevBar = ({ dev, max = 250 }) => {
  const pct = Math.min(Math.abs(dev || 0) / max, 1) * 50;
  return (
    <span className="inline-flex h-2 w-24 items-center rounded-full bg-thhover/8">
      <span className="relative h-2 flex-1">
        <span className="absolute h-2 rounded-full" style={{
          left: dev >= 0 ? "50%" : `${50 - pct}%`, width: `${pct}%`,
          background: dev >= 0 ? "#F43F5E" : "#10B981",
        }} />
      </span>
    </span>
  );
};

const AgentCard = ({ a, rank, worst, filters }) => {
  const [open, setOpen] = useState({});
  const [subs, setSubs] = useState({});
  const color = worst ? "#F43F5E" : "#10B981";

  const expand = async (cat) => {
    if (open[cat]) { setOpen({ ...open, [cat]: false }); return; }
    setOpen({ ...open, [cat]: true });
    if (!subs[cat]) {
      const { data } = await api.get(`/aht/agents/${encodeURIComponent(a.login)}/breakdown?category=${encodeURIComponent(cat)}&${fq(filters)}`);
      setSubs((s) => ({ ...s, [cat]: data.rows }));
    }
  };
  const dlCalls = (cat = "") => downloadPost(`/aht/agents/${encodeURIComponent(a.login)}/calls-excel?category=${encodeURIComponent(cat)}`, null, `${a.login}_calls.xlsx`, "get");

  return (
    <div data-testid={`aht-agent-card-${a.login}`} className={`${CARD} mb-5 p-5`} style={{ borderColor: worst ? "rgb(var(--th-coral) / 0.4)" : "rgb(var(--th-mint) / 0.4)" }}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white" style={{ background: color }}>{rank}</span>
          <div>
            <p className="font-heading text-sm font-bold text-thstrong">{a.login}</p>
            <p className="text-[11px] text-thmute">{a.name !== a.login ? `${a.name} · ` : ""}{a.lob} · <span className="rounded bg-teal-50 px-1.5 py-0.5 text-teal-600">{a.location}</span></p>
          </div>
        </div>
        <div className="text-right">
          <span className="font-mono-hud rounded-lg px-3 py-1 text-xl font-bold" style={{ background: worst ? "rgb(var(--th-coral) / 0.15)" : "rgb(var(--th-mint) / 0.15)", color }}>
            {fmtAht(a.aht)} <span className="text-xs">({Math.round(a.aht)}s)</span>
          </span>
          <p className="mt-1 text-[11px] text-thdim">{fmtNum(a.calls)} calls · {fmtDelta(a.vs_avg)} vs overall avg</p>
          <button onClick={() => dlCalls()} data-testid={`aht-agent-dl-${a.login}`}
            className="mt-1.5 rounded-lg bg-thblue px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-thblue">Download All Calls</button>
        </div>
      </div>

      <p className={`${LBL} mb-2`}>Category & Sub-Category · AHT vs {a.lob} LOB benchmark · click ▶ to expand</p>
      <table className="w-full text-left">
        <thead><tr className="border-b border-thborder/8">
          <th className={`${LBL} py-2`}>Category / Sub Category</th>
          <th className={`${LBL} py-2 text-right`}>Calls</th>
          <th className={`${LBL} py-2 text-center`}>Agent AHT</th>
          <th className={`${LBL} py-2 text-center`}>{a.lob} Cat/Sub Avg</th>
          <th className={`${LBL} py-2 text-right`}>vs {a.lob}</th>
          <th className={`${LBL} py-2 text-right`}>Export</th>
        </tr></thead>
        <tbody>
          {a.categories.map((c) => (
            <Fragment key={c.name}>
              <tr className="border-b border-thborder/6">
                <td className="py-2">
                  <button onClick={() => expand(c.name)} className="flex items-center gap-1.5 text-xs font-semibold text-thbody hover:text-thcyan">
                    {open[c.name] ? <ChevronDown className="h-3.5 w-3.5 text-amber-500" /> : <ChevronRight className="h-3.5 w-3.5 text-amber-500" />}
                    {c.name}
                  </button>
                </td>
                <td className="font-mono-hud py-2 text-right text-xs text-thmute">{fmtNum(c.calls)}</td>
                <td className="py-2 text-center"><span className="font-mono-hud rounded px-2 py-1 text-xs font-bold" style={{ background: c.dev > 0 ? "rgb(var(--th-coral) / 0.15)" : "rgb(var(--th-mint) / 0.15)", color: c.dev > 0 ? "rgb(var(--th-coral))" : "rgb(var(--th-mint))" }}>{fmtAht(c.aht)}</span></td>
                <td className="font-mono-hud py-2 text-center text-xs text-thmute">{fmtAht(c.lob_avg)} <span className="text-thfaint">({Math.round(c.lob_avg || 0)}s)</span></td>
                <td className="py-2 text-right">
                  <span className={`font-mono-hud mr-2 text-xs font-bold ${c.dev > 0 ? "text-red-500" : "text-thmint"}`}>{fmtDelta(c.dev)}</span>
                  <DevBar dev={c.dev} />
                </td>
                <td className="py-2 text-right">
                  <button onClick={() => dlCalls(c.name)} className="rounded border border-thborder/12 px-2 py-1 text-[10px] text-thmute hover:text-thcyan">XLSX</button>
                </td>
              </tr>
              {open[c.name] && (subs[c.name] || []).map((s) => (
                <tr key={c.name + s.name} className="border-b border-thborder/6 bg-thhover/5/50">
                  <td className="py-1.5 pl-8 text-[11px] text-thbody">{s.name}</td>
                  <td className="font-mono-hud py-1.5 text-right text-[11px] text-thdim">{fmtNum(s.calls)}</td>
                  <td className="py-1.5 text-center"><span className="font-mono-hud rounded px-2 py-0.5 text-[11px] font-bold" style={{ background: s.dev > 0 ? "rgb(var(--th-coral) / 0.15)" : "rgb(var(--th-mint) / 0.15)", color: s.dev > 0 ? "rgb(var(--th-coral))" : "rgb(var(--th-mint))" }}>{fmtAht(s.aht)}</span></td>
                  <td className="font-mono-hud py-1.5 text-center text-[11px] text-thdim">{fmtAht(s.lob_avg)}</td>
                  <td className="py-1.5 text-right"><span className={`font-mono-hud mr-2 text-[11px] font-bold ${s.dev > 0 ? "text-red-500" : "text-thmint"}`}>{fmtDelta(s.dev)}</span><DevBar dev={s.dev} /></td>
                  <td />
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>

      {a.monthly.length > 0 && (
        <div className="mt-4 rounded-xl border border-thborder/8 p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className={LBL}>Month-on-Month AHT</p>
            <Spark series={a.monthly.map((m) => m.aht)} width={120} color="#F59E0B" />
          </div>
          <div className="flex gap-3 overflow-x-auto">
            {a.monthly.map((m) => (
              <div key={m.month} className="w-28 shrink-0 rounded-lg border border-thborder/8 p-2.5">
                <p className="font-mono-hud text-[9px] text-thdim">{m.month}</p>
                <p className="font-mono-hud text-base font-bold" style={{ color }}>{fmtAht(m.aht)}</p>
                <p className="text-[9px] text-thdim">{fmtNum(m.calls)} calls</p>
                <div className="mt-1 h-1 rounded-full" style={{ background: color }} />
                {m.delta != null && <p className={`font-mono-hud mt-1 text-[9px] font-bold ${m.delta > 0 ? "text-red-500" : "text-thmint"}`}>{m.delta > 0 ? "↑" : "↓"} {fmtDelta(m.delta)}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const AgentsTab = ({ summary, filters, setFilters }) => {
  const [data, setData] = useState(null);
  const [minCalls, setMinCalls] = useState(20);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setData(null);
    api.get(`/aht/agents-analysis?min_calls=${minCalls}&${fq(filters)}`).then((r) => setData(r.data));
  }, [filters, minCalls]);
  useEffect(() => { load(); }, [load]);

  if (!summary.has_login) return <p className="py-20 text-center text-sm text-thdim">Agents view requires a Login column in uploads.</p>;
  if (!data) return <Loading />;
  const k = data.kpis;

  const filtered = data.monthly_table.filter((r) =>
    !search || [r.login, r.name, r.lob, r.location].join(" ").toLowerCase().includes(search.toLowerCase()));
  const pages = Math.ceil(filtered.length / perPage);
  const pageRows = filtered.slice(page * perPage, (page + 1) * perPage);
  const lastMonth = data.months[data.months.length - 1];

  const exportAgents = async () => {
    setBusy(true);
    await downloadPost("/aht/export/excel", {
      filename: "AHT_Agents.xlsx",
      sheets: [{ name: "Agents", columns: ["Agent", "Name", "LOB", "Location", "Calls", "Avg AHT (s)", ...data.months, "vs Prev (s)"],
        rows: filtered.map((r) => [r.login, r.name, r.lob, r.location, r.calls, Math.round(r.aht),
          ...data.months.map((m) => (r.cells[m] != null ? Math.round(r.cells[m]) : "")), r.vs_prev != null ? Math.round(r.vs_prev) : ""]) }],
    }, "AHT_Agents.xlsx");
    setBusy(false);
  };

  return (
    <div>
      <h2 className="font-heading mb-5 text-xl font-bold text-thstrong">Agent AHT Analysis</h2>
      <div className="flex flex-wrap items-end gap-3">
        <FiltersBar summary={summary} filters={filters} setFilters={setFilters} />
        <div className="mb-6">
          <label className={`${LBL} mb-1.5 block`}>Min Calls</label>
          <input data-testid="aht-agents-min-calls" type="number" min="1" value={minCalls}
            onChange={(e) => setMinCalls(Number(e.target.value) || 1)} className={`${SEL} w-20`} />
        </div>
      </div>

      <div className="mb-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi testId="aht-agents-total" label="Total Agents" value={fmtNum(k.total)} sub={`With ${minCalls}+ calls: ${fmtNum(k.eligible)}`} accent="rgb(var(--th-strong))" />
        <Kpi testId="aht-agents-avg" label="Overall Avg AHT" value={fmtAht(k.aht)} sub={`${Math.round(k.aht || 0)}s across filtered data`} accent="rgb(var(--th-strong))" />
        <Kpi testId="aht-agents-highest" label="Highest Agent AHT" value={fmtAht(k.highest?.aht)} sub={k.highest?.login} accent="#F43F5E" />
        <Kpi testId="aht-agents-lowest" label="Lowest Agent AHT" value={fmtAht(k.lowest?.aht)} sub={k.lowest?.login} accent="#10B981" />
      </div>

      <p className="mb-3 text-sm font-bold text-red-500">Top 10 Highest AHT Agents <span className="font-normal text-thdim">(min {minCalls} calls, ranked by impact)</span></p>
      {data.top_worst.map((a, i) => <AgentCard key={a.login} a={a} rank={i + 1} worst filters={filters} />)}

      <p className="mb-3 mt-8 text-sm font-bold text-thmint">Top 5 Best Performing Agents <span className="font-normal text-thdim">(min {minCalls} calls, ranked by impact)</span></p>
      {data.top_best.map((a, i) => <AgentCard key={a.login} a={a} rank={i + 1} filters={filters} />)}

      <div className={`${CARD} mt-8 p-6`}>
        <p className="text-sm font-bold text-thstrong">Agent Performance Scatter <span className="font-normal text-thdim">AHT (horizontal) vs Call Volume (vertical) · outliers are upper-right</span></p>
        <div className="mt-4 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 30, bottom: 20, left: 10 }}>
              <XAxis dataKey="aht" name="AHT" unit="s" tick={{ fill: "#7a8fb5", fontSize: 10, fontFamily: "JetBrains Mono" }} label={{ value: "Average AHT (seconds)", position: "bottom", fontSize: 11, fill: "#7a8fb5" }} />
              <YAxis dataKey="calls" name="Calls" tick={{ fill: "#7a8fb5", fontSize: 10, fontFamily: "JetBrains Mono" }} label={{ value: "Call Volume", angle: -90, position: "insideLeft", fontSize: 11, fill: "#7a8fb5" }} />
              <ZAxis range={[50, 51]} />
              <ReferenceLine x={Math.round(data.avg)} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: `avg ${Math.round(data.avg)}s`, fontSize: 10, fill: "#F59E0B", position: "top" }} />
              <Tooltip formatter={(v, n) => (n === "AHT" ? fmtAht(v) : fmtNum(v))} content={({ payload }) => payload?.[0] ? (
                <div className="rounded-lg border border-thborder/12 bg-thcard px-3 py-2 text-xs shadow">
                  <b>{payload[0].payload.login}</b><br />AHT {fmtAht(payload[0].payload.aht)} · {fmtNum(payload[0].payload.calls)} calls
                </div>) : null} />
              {Object.entries(BUCKET).map(([b, color]) => (
                <Scatter key={b} data={data.scatter.filter((s) => s.bucket === b)} fill={color} fillOpacity={0.75} />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex gap-4 text-[11px] text-thmute">
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500" />+30s above avg</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-amber-500" />Above avg</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-thdim" />Near avg</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-thmint" />15s+ below avg</span>
        </div>
      </div>

      <div className={`${CARD} mt-8 overflow-x-auto`}>
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4">
          <p className="text-sm font-bold text-thstrong">All Agents <span className="font-normal text-thdim">{fmtNum(filtered.length)} agents · ranked by AHT</span>
            {lastMonth && <span className="ml-2 rounded bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-600">Latest: {lastMonth} </span>}
          </p>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-thdim" />
              <input data-testid="aht-agents-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search agent / LOB / location..." className={`${SEL} w-56 pl-8`} />
            </div>
            <select data-testid="aht-agents-perpage" value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }} className={SEL}>
              {[25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <ExpBtn testId="aht-export-agents" onClick={exportAgents} busy={busy} color="purple">Export Agents</ExpBtn>
          </div>
        </div>
        <table className="mt-3 w-full text-left" data-testid="aht-agents-table">
          <thead><tr className="border-b border-thborder/8">
            <th className={`${LBL} px-4 py-2`}>#</th>
            <th className={`${LBL} px-4 py-2`}>Agent</th>
            <th className={`${LBL} px-4 py-2`}>LOB</th>
            <th className={`${LBL} px-4 py-2`}>Location</th>
            <th className={`${LBL} px-4 py-2 text-right`}>Calls</th>
            <th className={`${LBL} px-4 py-2 text-center`}>Avg AHT</th>
            {data.months.map((m) => <th key={m} className={`${LBL} px-2 py-2 text-center ${m === lastMonth ? "text-amber-500" : ""}`}>{m}{m === lastMonth ? " " : ""}</th>)}
            <th className={`${LBL} px-4 py-2 text-right`}>vs Prev</th>
          </tr></thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={r.login} className="border-b border-thborder/6 hover:bg-thhover/5/60">
                <td className="font-mono-hud px-4 py-2 text-xs text-thfaint">{page * perPage + i + 1}</td>
                <td className="px-4 py-2 text-xs font-bold text-thbody">{r.login}</td>
                <td className="px-4 py-2 text-xs text-thmute">{r.lob}</td>
                <td className="px-4 py-2 text-xs text-thmute">{r.location}</td>
                <td className="font-mono-hud px-4 py-2 text-right text-xs text-thmute">{fmtNum(r.calls)}</td>
                <td className="px-4 py-2 text-center"><span className="font-mono-hud rounded px-2 py-1 text-xs font-bold" style={{ background: r.aht > data.avg ? "rgb(var(--th-coral) / 0.15)" : "rgb(var(--th-mint) / 0.15)", color: r.aht > data.avg ? "rgb(var(--th-coral))" : "rgb(var(--th-mint))" }}>{fmtAht(r.aht)} ({Math.round(r.aht)}s)</span></td>
                {data.months.map((m) => (
                  <td key={m} className="px-2 py-2 text-center">
                    {r.cells[m] != null ? <span className={`font-mono-hud rounded px-1.5 py-0.5 text-[11px] font-bold ${m === lastMonth ? "border border-amber-200" : ""}`}
                      style={{ background: r.cells[m] > data.avg ? "rgb(var(--th-coral) / 0.15)" : "rgb(var(--th-mint) / 0.15)", color: r.cells[m] > data.avg ? "rgb(var(--th-coral))" : "rgb(var(--th-mint))" }}>{fmtAht(r.cells[m])}</span>
                      : <span className="text-xs text-thfaint">—</span>}
                  </td>
                ))}
                <td className={`font-mono-hud px-4 py-2 text-right text-xs font-bold ${r.vs_prev > 0 ? "text-red-500" : r.vs_prev < 0 ? "text-thmint" : "text-thfaint"}`}>
                  {r.vs_prev != null ? `${r.vs_prev > 0 ? "↑" : "↓"} ${fmtDelta(r.vs_prev)}` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-5 py-3">
          <p className="text-xs text-thdim">Page {page + 1} of {Math.max(pages, 1)}</p>
          <div className="flex gap-2">
            <button data-testid="aht-agents-prev" disabled={page === 0} onClick={() => setPage(page - 1)} className="rounded-lg border border-thborder/12 px-3 py-1.5 text-xs disabled:opacity-30">← Prev</button>
            <button data-testid="aht-agents-next" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="rounded-lg border border-thborder/12 px-3 py-1.5 text-xs disabled:opacity-30">Next →</button>
          </div>
        </div>
      </div>
    </div>
  );
};
